import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { TTS_API_URL } from "routes/api";
import { getTokenFallback } from "redux_dir/features/apiMananger";

/**
 * Lecture d'un texte en français.
 *
 * On demande d'abord le MP3 au backend (/tts/) : la voix est alors identique
 * sur tous les appareils. C'est indispensable car window.speechSynthesis
 * dépend du moteur vocal du téléphone, et sur les appareils sans pack de voix
 * française le texte est lu par une voix anglaise, donc inaudible.
 * b
 * Si le serveur est injoignable, on retombe sur la synthèse du navigateur :
 * dégradée, mais mieux que pas de son du tout.
 */

const buildTtsUrl = (text, speed) =>
  `${TTS_API_URL}tts/?text=${encodeURIComponent(text)}&speed=${speed}`;

const getBestFrenchVoice = () => {
  if (!window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  return (
    voices.find((v) => v.lang === "fr-FR" || v.lang === "fr_FR" || v.lang === "fr") ||
    voices.find((v) => v.lang?.toLowerCase().startsWith("fr")) ||
    null
  );
};

const useFrenchTTS = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);

  const audioRef = useRef(null);
  // { key, url } du dernier MP3 téléchargé, pour ne pas le redemander
  const preparedRef = useRef(null);

  const tokenFromStore = useSelector(
    (state) => state?.user?.userReducer?.userInfo?.token
  );

  const getAudio = useCallback(() => {
    if (!audioRef.current) {
      const audio = new Audio();
      audio.onended = () => setIsPlaying(false);
      audio.onerror = () => setIsPlaying(false);
      audioRef.current = audio;
    }
    return audioRef.current;
  }, []);

  const releasePrepared = useCallback(() => {
    if (preparedRef.current?.url) {
      URL.revokeObjectURL(preparedRef.current.url);
    }
    preparedRef.current = null;
  }, []);

  /**
   * Télécharge le MP3 à l'avance. Appelé dès que le texte est connu pour que
   * le clic sur "Écouter" déclenche play() immédiatement : iOS Safari refuse
   * de jouer un son si l'appel play() arrive trop tard après le geste
   * utilisateur (c'est-à-dire après un await réseau).
   */
  const prepare = useCallback(
    async (text, { speed = 1 } = {}) => {
      if (!text?.trim()) return null;

      const key = `${text}|${speed}`;
      if (preparedRef.current?.key === key) return preparedRef.current.url;

      const token = tokenFromStore || getTokenFallback();
      setIsLoading(true);
      try {
        const response = await fetch(buildTtsUrl(text, speed), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error(`TTS HTTP ${response.status}`);

        const blob = await response.blob();
        releasePrepared();
        const url = URL.createObjectURL(blob);
        preparedRef.current = { key, url };
        setUsedFallback(false);
        return url;
      } catch (error) {
        console.warn("TTS serveur indisponible, repli sur la voix du navigateur:", error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [tokenFromStore, releasePrepared]
  );

  /**
   * Joue un fichier audio pré-enregistré (ex : audio_dp[i] de l'épreuve).
   * Retourne true si la lecture a démarré, false sinon — l'appelant retombe
   * alors sur la chaîne TTS serveur puis navigateur, comme sans fichier.
   */
  const speakUrl = useCallback(
    async (url) => {
      if (!url) return false;
      const audio = getAudio();
      try {
        if (audio.src !== url) audio.src = url;
        audio.currentTime = 0;
        setIsPlaying(true);
        await audio.play();
        setUsedFallback(false);
        return true;
      } catch (error) {
        console.warn("Lecture du fichier audio impossible, repli TTS:", error);
        setIsPlaying(false);
        return false;
      }
    },
    [getAudio]
  );

  /** Précharge un fichier audio pour qu'il démarre instantanément au clic. */
  const preloadUrl = useCallback(
    (url) => {
      if (!url) return;
      const audio = getAudio();
      if (audio.src !== url) {
        audio.src = url;
        audio.load();
      }
    },
    [getAudio]
  );

  /** Synthèse vocale du navigateur, utilisée uniquement en dernier recours. */
  const speakWithBrowser = useCallback((text, speed) => {
    if (!window.speechSynthesis || typeof SpeechSynthesisUtterance === "undefined") {
      setIsPlaying(false);
      return;
    }
    setUsedFallback(true);
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    const voice = getBestFrenchVoice();
    if (voice) utterance.voice = voice;
    utterance.lang = "fr-FR";
    utterance.rate = speed;
    utterance.volume = 1;
    utterance.onend = () => setIsPlaying(false);
    utterance.onerror = () => setIsPlaying(false);

    setIsPlaying(true);
    window.speechSynthesis.speak(utterance);
  }, []);

  const speak = useCallback(
    async (text, { speed = 1 } = {}) => {
      if (!text?.trim()) return;

      const key = `${text}|${speed}`;
      const url =
        preparedRef.current?.key === key
          ? preparedRef.current.url
          : await prepare(text, { speed });

      if (!url) {
        speakWithBrowser(text, speed);
        return;
      }

      const audio = getAudio();
      try {
        if (audio.src !== url) audio.src = url;
        audio.currentTime = 0;
        setIsPlaying(true);
        await audio.play();
      } catch (error) {
        // Lecture refusée (autoplay bloqué, format non supporté...)
        console.warn("Lecture du MP3 impossible, repli sur la voix du navigateur:", error);
        speakWithBrowser(text, speed);
      }
    },
    [prepare, getAudio, speakWithBrowser]
  );

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setIsPlaying(false);
  }, []);

  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.pause();
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      if (preparedRef.current?.url) URL.revokeObjectURL(preparedRef.current.url);
    };
  }, []);

  return { speak, prepare, speakUrl, preloadUrl, stop, isLoading, isPlaying, usedFallback };
};

export default useFrenchTTS;
